import { Box, Typography, Stack, Button } from '@mui/material';
import { Card } from 'src/components/UI/Card';
import { DataTable, type Column } from 'src/components/UI/DataTable';
import { CitationReportStyles } from '../../styles/citationReport';
import { useLocation, useNavigate } from 'react-router-dom';
import { PDFExport } from '@progress/kendo-react-pdf';
import { useEffect, useRef, useState, useMemo } from 'react';
import { CircularProgress } from '@mui/material';
import { citationReport } from 'src/common/Scraper';
import { LocalSeoCommonLoader } from 'src/content/localSeo/LocalSeoCommonLoader';
import dayjs from 'dayjs';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'src/redux/store';
import { timeConverter } from 'src/common/config/FunctionHelper';
import { drawDOM, exportPDF } from '@progress/kendo-drawing';
import { useEmailReport } from '../../hooks/useEmailReport';
import { StripeLoader } from 'src/common/StripeLoader';
import { useCitation } from '../../Providers/CitationProvider';

interface CitationRow {
  id: number;
  citationSite: string;
  status: string;
  businessName: string;
  address: string;
  profileUrl: string;
  trend: string | number;
  removedDuplicates: string;
  type: string;
}

const getValidStatusColor = (row: CitationRow) => {
  switch(row.status.toLowerCase()) {
    case 'live':
      return () => (
        <>
            <Box sx={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              backgroundColor: 'var(--color-success-medium)' // solid green inner circle
            }} />
            <Typography sx={CitationReportStyles.statusText}>
              {row.status}
            </Typography>
          </>
      );
    case 'updated':
      return () => (
        <>
            <Box
              component="img"
              src="/static/images/icons/Icon-update.svg"
              width={16}
              height={16}
              alt="updated"
            />
            <Typography sx={CitationReportStyles.statusText}>
              {row.status}
            </Typography>
          
          </>
      );
    default:
      return () => (
        <>
            <Box
              component="img"
              src="/static/images/icons/todoIcon.svg"
              width={16}
              height={16}
              alt="updated"
            />
            <Typography sx={{...CitationReportStyles.statusText, color: 'var(--color-warning-orange)'}}>
              {row.status}
            </Typography>
          </>
      );
  }
}

const columns: Column<CitationRow>[] = [
  {
    id: 'citationSite',
    label: 'Citation Site',
    sortable: true,
    sortValue: (row) => row.citationSite,
    render: (row) => (
      <Typography sx={CitationReportStyles.citationSiteText}>
        {row.citationSite}
      </Typography>
    )
  },
  {
    id: 'status',
    label: 'Status',
    sortable: true,
    sortValue: (row) => row.status,
    render: (row) => (
      <Stack direction="row" spacing={1} alignItems="center">
        {getValidStatusColor(row)()}
      </Stack>
    )
  },
  {
    id: 'address',
    label: 'Business Address',
    render: (row) => (
      <Box>
        <Typography sx={CitationReportStyles.businessNameText}>
          {row.businessName}
        </Typography>
        <Typography sx={CitationReportStyles.businessAddressText}>
          {row.address}
        </Typography>
      </Box>
    )
  },
  {
    id: 'trend',
    label: 'Trend',
    render: (row) => (
      <Box>
        <Typography sx={CitationReportStyles.directoryVisitsText}>
          Directory&apos;s monthly visits: {row.trend}
        </Typography>
      </Box>
    )
  },
  {
    id: 'removedDuplicates',
    label: 'Removed Duplicated Listings',
    align: 'center',
    render: (row) => (
      <Typography sx={CitationReportStyles.removedDuplicatesText}>
        {row.removedDuplicates}
      </Typography>
    )
  }
];


export const CitationReport = () => {
  const {
    state: { campaignId }
  } = useLocation() as { state: { campaignId: number } };
  const navigate = useNavigate();
  const { setError } = useCitation();
  const { mutateAsync: emailReport } = useEmailReport();
  const pdfExportComponent = useRef<PDFExport>(null);
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isEmailing, setIsEmailing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState<any>(null);
  let userDetails: any = useSelector(
    (state: RootState) => state?.counter?.userDetails
  );

  const handleDownloadPdf = () => {
    setIsDownloading(true);
    // Allow time for the hidden PDF container to render
    setTimeout(() => {
      if (pdfExportComponent.current) {
        pdfExportComponent.current.save();
        // Reset state after save
        setTimeout(() => {
          setIsDownloading(false);
        }, 1000);
      }
    }, 500);
  };

  const handleEmailReport = async () => {
    if (!campaignId || !pdfContainerRef.current) {
      setError({
          status: 'error',
          message: 'Unable to generate PDF. Please try again.'
        })
      return;
    }

    setIsEmailing(true);
    try {
      // Wait for PDF container to be ready
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Get the PDF container element
      const pdfElement = pdfContainerRef.current.querySelector('.PDF') as HTMLElement;
      if (!pdfElement) {
        throw new Error('PDF container not found');
      }

      // Generate PDF
      const drawing = await drawDOM(pdfElement, {
        paperSize: 'A4',
        scale: 0.5
      });

      const pdfDataUri = await exportPDF(drawing);

      // Extract PDF URI from data URI (format: data:application/pdf;base64,<base64string>)
      const pdfURI = pdfDataUri.split(',')[1] || pdfDataUri;

      // Send email with PDF
      await emailReport({ reportPdfURI: pdfURI });

      setError({
          status: 'success',
          message: 'Report has been sent to your email successfully!'
        });
    } catch (error) {
      setError({
          status: 'error',
          message: 'Failed to send email report. Please try again.'
        });
    } finally {
      setIsEmailing(false);
    }
  };

  useEffect(() => {
    if (campaignId) {
      getCitationReport();
    }
  }, [campaignId]);

  const getCitationReport = async () => {
    setLoading(true);
    try {
      const response: any = await citationReport(campaignId);
      if (response?.status === 'success' && response?.response) {
        const reportData = response.response;
        setReportData(reportData);
      } else {
        setReportData(null);
        setError({
            status: 'error',
            message: 'Failed to fetch citation report. Please try again.'
          });
      }
    } catch (error) {
      console.error('Error fetching citation report:', error);
    } finally {
      setLoading(false);
    }
  };

  const rows = useMemo(() => {
    if (!reportData?.reportList) return [];
    return reportData.reportList.map((item: any, index: number) => ({
      ...item,
      status: item.status.replace('_', ' '),
      id: index
    }));
  }, [reportData]);

  const reportDate = timeConverter(
    reportData?.reportDate || '',
    userDetails?.date_format || 'DD/MM/YYYY',
    true
  );

  if (loading) {
    return <StripeLoader />
  }

  if(reportData?.reportList?.length === 0) {
    return (
      <Card sx={{
        ...CitationReportStyles.card,
      }}>
        <Typography sx={{...CitationReportStyles.heading, textAlign: 'center'}}>No report data found</Typography>
      </Card>
    );
  }

  return (
    <>
      <Card sx={CitationReportStyles.card}>
        <Box sx={CitationReportStyles.headerBox}>
          <Typography sx={CitationReportStyles.heading}>
            Campaign Report "{reportData?.campaignName}" {campaignId}
          </Typography>
          <Typography sx={CitationReportStyles.subHeading}>
            Report generated on {reportDate}
          </Typography>
        </Box>

        <Box sx={CitationReportStyles.tableContainer}>
          <DataTable<CitationRow>
            data={rows}
            columns={columns}
            getRowId={(row) => row.id}
            pagination={rows.length > 10}
            rowsPerPageOptions={[10, 25, 50, 100]}
            defaultRowsPerPage={10}
          />
        </Box>

        <Box sx={CitationReportStyles.actionsContainer}>
          <Button
            variant="contained"
            color="primary"
            sx={CitationReportStyles.primaryButton}
            onClick={handleDownloadPdf}
            disabled={isDownloading}
            startIcon={
              isDownloading ? (
                <CircularProgress size={20} color="inherit" />
              ) : null
            }
          >
            {isDownloading ? 'Downloading...' : 'Download PDF'}
          </Button>
          <Button
            variant="outlined"
            color="primary"
            sx={CitationReportStyles.secondaryButton}
            onClick={handleEmailReport}
            disabled={isEmailing || !reportData}
            startIcon={
              isEmailing ? (
                <CircularProgress size={20} color="inherit" />
              ) : null
            }
          >
            {isEmailing ? 'Sending...' : 'Email Me This Report'}
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => {
              navigate('/local-seo/citations/list');
            }}
            sx={CitationReportStyles.lastButton}
          >
            Go To All Campaigns
          </Button>
        </Box>
      </Card>

      {/* Hidden PDF Export Container */}
      <div
        ref={pdfContainerRef}
        style={{
          position: 'absolute',
          left: '-10000px',
          top: 0
        }}
      >
        <PDFExport
          ref={pdfExportComponent}
          imageResolution={1500}
          paperSize="A4"
          scale={0.5}
          margin={0}
          fileName={`Citation_Report_${campaignId || 'SEOSpace'}.pdf`}
          forcePageBreak=".force-page-break"
        >
          <div
            className="PDF"
            style={{
              width: 'fit-content'
            }}
          >
            <Box
              className="p-4 "
              sx={{
                width: { xs: '1127px' },
                maxHeight: { xs: '1600px' },
                minWidth: { xs: '1127px' },
                background: 'var(--color-bg-cream)',
                paddingLeft: '2rem',
                paddingTop: '2rem',
                paddingBottom: '2rem',
                paddingRight: '2rem'
              }}
            >
              <div
                style={{
                  width: '20%',
                  height: '20%',
                  marginBottom: '10px',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                  overflow: 'hidden'
                }}
              >
                <img
                  src={
                    process.env.PUBLIC_URL + '/static/images/logo/new-logo.svg'
                  }
                  alt="Icon"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    background: 'transparent'
                  }}
                />
              </div>
              <Box sx={{ marginTop: '24px' }}>
                <Typography sx={CitationReportStyles.heading}>
                  Campaign Report "{reportData?.campaignName}" {campaignId}
                </Typography>
                <Typography sx={CitationReportStyles.subHeading}>
                  Report generated on {reportDate}
                </Typography>
              </Box>

              <Box sx={CitationReportStyles.tableContainer}>
                <DataTable<CitationRow>
                  data={rows}
                  columns={columns}
                  getRowId={(row) => row.id}
                  pagination={false}
                  defaultRowsPerPage={1000} // Show all rows in PDF
                  isPdf={true}
                />
              </Box>
            </Box>
          </div>
        </PDFExport>
      </div>
    </>
  );
};
